import "./gallery.css";

import React from "react";

import { GALLERY } from "../../data/content";
import { Chevron } from "../general/Icons";
import Lightbox from "../general/Lightbox";
import PageMeta from "../general/PageMeta";
import Tabs from "../general/Tabs";

//Sideways-scrolling row of photos with round previous / next buttons. Clicking a photo opens the full-screen viewer
const PhotoRow = ({ title, photos }) => {
  const [openIndex, setOpenIndex] = React.useState(null);
  const [canScroll, setCanScroll] = React.useState({ prev: false, next: false });
  const rowRef = React.useRef(null);

  const updateButtons = React.useCallback(() => {
    const row = rowRef.current;
    if (!row) {
      return;
    }
    setCanScroll({
      prev: row.scrollLeft > 4,
      next: row.scrollLeft + row.clientWidth < row.scrollWidth - 4,
    });
  }, []);

  React.useEffect(() => {
    updateButtons();
    window.addEventListener("resize", updateButtons);
    return () => window.removeEventListener("resize", updateButtons);
  }, [updateButtons]);

  const scroll = (direction) => {
    const row = rowRef.current;
    const item = row.firstElementChild;
    const step = item ? item.getBoundingClientRect().width + 32 : row.clientWidth;
    row.scrollBy({ left: direction * step, behavior: "smooth" });
  };

  return (
    <div className={"gallery-row-wrap"}>
      <ul
        ref={rowRef}
        className={"gallery-row"}
        onScroll={updateButtons}
      >
        {photos.map((photo, i) => (
          <li key={photo.id}>
            <button
              type={"button"}
              className={"gallery-item"}
              aria-label={`View ${photo.alt} full size`}
              onClick={() => setOpenIndex(i)}
            >
              <img
                src={photo.md}
                srcSet={photo.srcSet}
                sizes={"(max-width: 640px) 78vw, 468px"}
                alt={photo.alt}
                width={photo.width}
                height={photo.height}
                loading={"lazy"}
                onLoad={i === 0 ? updateButtons : undefined}
              />
            </button>
          </li>
        ))}
      </ul>
      {canScroll.prev ? (
        <button
          type={"button"}
          className={"gallery-arrow is-prev"}
          aria-label={"Previous photos"}
          onClick={() => scroll(-1)}
        >
          <Chevron />
        </button>
      ) : null}
      {canScroll.next ? (
        <button
          type={"button"}
          className={"gallery-arrow is-next"}
          aria-label={"More photos"}
          onClick={() => scroll(1)}
        >
          <Chevron isNext />
        </button>
      ) : null}
      <Lightbox
        title={title}
        photos={photos}
        index={openIndex}
        onClose={() => setOpenIndex(null)}
      />
    </div>
  );
};

const Gallery = () => {
  return (
    <React.Fragment>
      <PageMeta title={"Gallery"} />
      <h1 className={"visually-hidden"}>Gallery</h1>

      {GALLERY.map((section) => (
        <section
          key={section.ID}
          id={section.ID}
          className={"gallery-section"}
        >
          <h2 className={"section-title gallery-title"}>{section.TITLE}</h2>
          <Tabs
            id={`gallery-${section.ID}`}
            className={"is-gallery"}
            labels={section.TABS.map((t) => t.LABEL)}
            renderPanel={(i) => (
              <PhotoRow
                title={section.TABS[i].LABEL}
                photos={section.TABS[i].PHOTOS}
              />
            )}
          />
        </section>
      ))}
    </React.Fragment>
  );
};

export default Gallery;
